import { Box, Button, CircularProgress, IconButton, Menu, MenuItem, Typography } from "@mui/material";
import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from "react";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import ArrowDropUpIcon from "@mui/icons-material/ArrowDropUp";
import ZoomOutMapIcon from "@mui/icons-material/ZoomOutMap";
import ZoomInMapIcon from "@mui/icons-material/ZoomInMap";

import "./liveView.css";
import { useGetCamerasListQuery } from "../../redux/reducers/settings.reducer";
import { ICameraDetails, IGetEventsListResponseData } from "../../interfaces";
import { useGetEventsListQuery, useLazyGetEventFrameQuery } from "../../redux/reducers/dashboard.reducer";
import moment from "moment";
import { EVENT_LOGS_REQUEST_TIME_FORMAT } from "../../utils/constants";
import { ReactComponent as NoImageIcon } from "../../assets/icons/image-not-found-icon.svg";
import { ReactComponent as BikeIcon } from "../../assets/icons/bike-icon.svg";
import { ReactComponent as CarIcon } from "../../assets/icons/car-icon.svg";
import MVPCameraStream from "./MVPCameraStream";
import Stream from "./Stream";
import UNVCameraStream from "./UNVCameraStream";

interface IEventItemComponentProps {
	eventDetails: IGetEventsListResponseData;
	onFullscreen: (image: string) => void;
}

// const MAX_RETRY_COUNT = 5;

const EventItemComponent: FC<IEventItemComponentProps> = ({ eventDetails, onFullscreen }) => {
	const [getImage, { isLoading: getImageLoading, isFetching: getImageFetching, data: imageData }] =
		useLazyGetEventFrameQuery();

	const LogType = useMemo<"Blacklisted" | "Visitor" | "Authorized" | "Unauthorized">(() => {
		if (eventDetails.is_blacklisted) return "Blacklisted";
		if (eventDetails.visitor) return "Visitor";
		if (eventDetails.auth) return "Authorized";
		return "Unauthorized";
	}, [eventDetails]);

	const LogTypeColor = useMemo<string>(() => {
		if (LogType === "Authorized") return "var(--color-success-main)";
		if (LogType === "Blacklisted") return "var(--color-error-main)";
		if (LogType === "Unauthorized") return "var(--color-warning-main)";
		if (LogType === "Visitor") return "var(--color-info-main)";
		return "";
	}, [LogType]);

	useEffect(() => {
		if (eventDetails) {
			getImage({ id: eventDetails.id, data: { frame_type: "image" } }, true);
		}
	}, [eventDetails, getImage]);

	if (getImageLoading || getImageFetching) {
		return (
			<Box sx={{ 
				width: { xs: "200px", sm: "200px", md: "200px", lg: "200px", xl: "240px" }, 
				height: { xs: "115px", sm: "115px", md: "115px", lg: "115px", xl: "138px" }, 
				display: "flex", 
				alignItems: "center", 
				justifyContent: "center" 
			}}>
				<CircularProgress />
			</Box>
		);
	}

	if (imageData && imageData.frame_data) {
		return (
			<Box
				sx={{
					display: "flex",
					alignItems: "center",
					justifyContent: "center",
					position: "relative",
					width: { xs: "200px", sm: "200px", md: "200px", lg: "200px", xl: "245px" },
					height: { xs: "115px", sm: "115px", md: "115px", lg: "115px", xl: "138px" },
					borderRadius: "5px"
				}}
			>
				<img
					src={`data:image/png;base64,${imageData.frame_data}`}
					alt={eventDetails.number_plate}
					style={{ width: "100%", height: "100%", objectFit: "contain", borderRadius: "5px" }}
				/>

				<Box
					sx={{
						position: "absolute",
						top: 0,
						right: 0,
						backgroundColor: LogTypeColor,
						paddingX: "0.5rem",
						borderRadius: "5px"
					}}
				>
					<Typography variant="body2" color="white" sx={{ fontSize: { xs: "0.7rem", sm: "0.7rem", md: "0.7rem", lg: "0.7rem", xl: "0.875rem" } }}>
						{LogType}
					</Typography>
				</Box>

				<Box
					sx={{
						position: "absolute",
						top: 0,
						left: 0,
						backgroundColor: "black",
						paddingX: "0.5rem",
						borderRadius: "5px"
					}}
				>
					<Typography variant="body2" color="white" sx={{ fontSize: { xs: "10px", sm: "10px", md: "10px", lg: "10px", xl: "12px" } }}>
						{moment(eventDetails.timestamp).format("YY-MM-DD HH:mm:ss")}
					</Typography>
				</Box>

				<Box
					sx={{
						position: "absolute",
						left: 0,
						right: 0,
						bottom: 0,
						backgroundColor: "black",
						display: "flex",
						alignItems: "center",
						justifyContent: "space-between",
						borderRadius: "5px",
						paddingLeft: "0.75rem",
						paddingRight: "0.5rem"
					}}
				>
					{/* <Box sx={{ display: "flex", alignItems: "center", gap: "0.5rem" }}> */}
					{eventDetails.vehicle_type === 0 ? <CarIcon width={22} fill="white" /> : <BikeIcon width={22} fill="white" />}

					<Typography color="white" sx={{ fontSize: { xs: "14px", sm: "14px", md: "14px", lg: "14px", xl: "16px" } }}>{`${eventDetails.number_plate}`}</Typography>
					{/* </Box> */}

					<IconButton size="small" onClick={() => onFullscreen(imageData.frame_data)}>
						<ZoomOutMapIcon sx={{ color: "white" }} fontSize="inherit" />
					</IconButton>
				</Box>
			</Box>
		);
	}

	return (
		<Box
			sx={{
				display: "flex",
				flexDirection: "column",
				alignItems: "center",
				justifyContent: "center",
				// height: "100%",
				gap: "0.5rem",
				width: { xs: "200px", sm: "200px", md: "200px", lg: "200px", xl: "240px" },
				height: { xs: "115px", sm: "115px", md: "115px", lg: "115px", xl: "138px" }
			}}
		>
			<NoImageIcon width={30} fill="#d5d5d5" stroke="#d5d5d5" />

			<Typography variant="body1" color="#999999" sx={{ fontSize: { xs: "0.875rem", sm: "0.875rem", md: "0.875rem", lg: "0.875rem", xl: "1rem" } }}>
				Image not available
			</Typography>
		</Box>
	);
};

const LiveView: FC = () => {
	const cameraSelectionDropdownButtonRef = useRef<HTMLButtonElement>(null);

	const [cameraSelectionDropdownOpen, setCameraSelectionDropdownOpen] = useState<boolean>(false);
	const [selectedCamera, setSelectedCamera] = useState<ICameraDetails | null>(null);
	const [fullscreenImage, setFullscreenImage] = useState<string>("");
	// const [showVideoLoader, setShowVideoLoader] = useState<boolean>(false);
	// const [showVideoError, setShowVideoError] = useState<boolean>(false);

	// APIS
	// const [getCameraStreamStatus] = useLazyGetCameraStreamStatusQuery();

	const {
		data: getCamerasListResponse,
		isLoading: getCamerasListLoading,
		isFetching: getCamerasListFetching
	} = useGetCamerasListQuery({});

	// const {
	// 	data: getCameraStreamUrlResponse,
	// 	isLoading: getCameraStreamUrlLoading,
	// 	isFetching: getCameraStreamUrlFetching
	// } = useGetCameraStreamUrlQuery(selectedCamera?.id ?? -1, {
	// 	skip: !selectedCamera
	// });

	const {
		data: getEventsListResponse,
		isLoading: getEventsListLoading,
		isFetching: getEventsListFetching
	} = useGetEventsListQuery(
		{
			start_date: moment().startOf("day").format(EVENT_LOGS_REQUEST_TIME_FORMAT),
			end_date: moment().endOf("day").format(EVENT_LOGS_REQUEST_TIME_FORMAT),
			page: 1,
			camera_id: selectedCamera?.camera_id
		},
		{ pollingInterval: 15 * 1000, skip: !selectedCamera }
	);

	function handleOpenCameraSelectionDropdown() {
		setCameraSelectionDropdownOpen(true);
	}

	function handleCloseCameraSelectionDropdown() {
		setCameraSelectionDropdownOpen(false);
	}

	function handleChangeSelectedCamera(cameraDetails: ICameraDetails) {
		setSelectedCamera(cameraDetails);
		handleCloseCameraSelectionDropdown();
	}

	function handleShowFullscreenImage(image: string) {
		setFullscreenImage(image);
	}

	function handleHideFullscreenImage() {
		setFullscreenImage("");
	}

	// function getStreamStatusHandler(cameraId: number, count = 0) {
	// 	if (count > MAX_RETRY_COUNT) {
	// 		setShowVideoLoader(false);
	// 		setShowVideoError(true);
	// 		return;
	// 	}

	// 	getCameraStreamStatus(cameraId)
	// 		.unwrap()
	// 		.then((response) => {
	// 			if (response.camera_status && response.live_stream_status && response.stream_file_generated) {
	// 				setShowVideoLoader(false);
	// 				setShowVideoError(false);
	// 			} else {
	// 				getStreamStatusHandler(cameraId, count + 1);
	// 			}
	// 		})
	// 		.catch((error) => {
	// 			console.error("Could not get camera stream status ::", error);
	// 			getStreamStatusHandler(cameraId, count + 1);
	// 		});
	// }

	const keypressEventListener = useCallback(
		(event: KeyboardEvent) => {
			if (event.key === "Escape") {
				if (fullscreenImage) handleHideFullscreenImage();
			}
		},
		[fullscreenImage]
	);

	useEffect(() => {
		document.addEventListener("keydown", keypressEventListener, false);
		return () => {
			document.removeEventListener("keydown", keypressEventListener, false);
		};
	}, [keypressEventListener]);

	useEffect(() => {
		if (getCamerasListResponse && getCamerasListResponse.results.length > 0) {
			setSelectedCamera((currentSelectedCamera) => {
				if (!currentSelectedCamera) return getCamerasListResponse.results[0];
				return currentSelectedCamera;
			});
		}
	}, [getCamerasListResponse]);

	// useEffect(() => {
	// 	if (getCameraStreamUrlLoading || getCameraStreamUrlFetching) {
	// 		setShowVideoLoader(true);
	// 	}
	// }, [getCameraStreamUrlLoading, getCameraStreamUrlFetching]);

	// useEffect(() => {
	// 	if (getCameraStreamUrlResponse) {
	// 		if (
	// 			selectedCamera &&
	// 			(!getCameraStreamUrlResponse.is_stream_created || !getCameraStreamUrlResponse.is_stream_running)
	// 		) {
	// 			getStreamStatusHandler(selectedCamera.id);
	// 		}
	// 	}
	// 	// eslint-disable-next-line react-hooks/exhaustive-deps
	// }, [getCameraStreamUrlResponse]);

	return (
		<Box sx={{ display: "flex", flexDirection: "column", gap: "1rem", flexGrow: 1 }}>
			<Box
				sx={{
					display: "flex",
					alignItems: "center",
					justifyContent: "flex-end",
					backgroundColor: "white",
					padding: { xs: "0.5rem", sm: "0.5rem", md: "0.5rem", lg: "0.5rem", xl: "0.5rem 1rem" },
					borderRadius: "4px"
				}}
			>
				<Button
					disableElevation
					variant="contained"
					color="primary"
					ref={cameraSelectionDropdownButtonRef}
					id="camera-selection-dropdown"
					aria-controls="camera-selection-dropdown-menu"
					aria-haspopup
					aria-expanded
					onClick={handleOpenCameraSelectionDropdown}
					endIcon={cameraSelectionDropdownOpen ? <ArrowDropUpIcon /> : <ArrowDropDownIcon />}
					sx={{ 
						minWidth: { xs: "140px", sm: "140px", md: "140px", lg: "140px", xl: "175px" }, 
						justifyContent: "space-between",
						fontSize: { xs: "0.875rem", sm: "0.875rem", md: "0.875rem", lg: "0.875rem", xl: "1rem" }
					}}
				>
					{selectedCamera ? selectedCamera.camera_site : "Select Camera"}
				</Button>

				<Menu
					id="camera-selection-dropdown-menu"
					anchorEl={cameraSelectionDropdownButtonRef.current}
					open={cameraSelectionDropdownOpen}
					onClose={handleCloseCameraSelectionDropdown}
					MenuListProps={{ "aria-labelledby": "camera-selection-dropdown" }}
					anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
					transformOrigin={{ horizontal: "right", vertical: "top" }}
				>
					{getCamerasListResponse && getCamerasListResponse.results.length > 0 ? (
						getCamerasListResponse.results.map((cameraItem) => (
							<MenuItem
								key={cameraItem.id}
								onClick={() => handleChangeSelectedCamera(cameraItem)}
								sx={{ minWidth: { xs: "140px", sm: "140px", md: "140px", lg: "140px", xl: "175px" } }}
							>
								{cameraItem.camera_site}
							</MenuItem>
						))
					) : (
						<MenuItem disabled sx={{ minWidth: { xs: "140px", sm: "140px", md: "140px", lg: "140px", xl: "175px" } }}>
							No Cameras
						</MenuItem>
					)}
				</Menu>
			</Box>

			<Box
				sx={{
					flexGrow: 1,
					display: "flex",
					flexDirection: { xs: "column", sm: "column", md: "column", lg: "column", xl: "row" },
					gap: "1rem",
					height: 0
				}}
			>
				<Box
					sx={{
						flex: 1,
						backgroundColor: "white",
						padding: { xs: "0.75rem", sm: "0.75rem", md: "0.75rem", lg: "0.75rem", xl: "1rem" },
						borderRadius: "4px",
						display: "flex",
						alignItems: "center",
						justifyContent: "center",
						overflowX: "hidden",
						minHeight: { xs: "200px", sm: "200px", md: "200px", lg: "200px", xl: "auto" }
					}}
				>
					{selectedCamera ? (
						selectedCamera.camera_type === "MVP" ? (
							<MVPCameraStream cameraId={selectedCamera.id} />
						) : selectedCamera.camera_type === "UNV" ? (
							<UNVCameraStream cameraId={selectedCamera.id} />
						) : (
							<Stream cameraId={selectedCamera.id} />
						)
					) : null}
				</Box>

				<Box
					sx={{
						backgroundColor: "white",
						padding: { xs: "0.75rem", sm: "0.75rem", md: "0.75rem", lg: "0.75rem", xl: "1rem" },
						borderRadius: "4px",
						overflow: "auto",
						width: { xs: "100%", sm: "100%", md: "100%", lg: "100%", xl: "auto" },
						minHeight: { xs: "140px", sm: "140px", md: "140px", lg: "140px", xl: "auto" }
					}}
				>
					{(getEventsListLoading || getEventsListFetching || getCamerasListLoading || getCamerasListFetching) &&
					(!getEventsListResponse || (getEventsListResponse && getEventsListResponse.results.length <= 0)) ? (
						<Box
							sx={{ 
								width: { xs: "100%", sm: "100%", md: "100%", lg: "100%", xl: "240px" }, 
								height: { xs: "100px", sm: "100px", md: "100px", lg: "100px", xl: "100%" }, 
								display: "flex", 
								alignItems: "center", 
								justifyContent: "center" 
							}}
						>
							<CircularProgress />
						</Box>
					) : getEventsListResponse && getEventsListResponse.results.length > 0 ? (
						<Box sx={{ 
							display: "flex", 
							flexDirection: { xs: "row", sm: "row", md: "row", lg: "row", xl: "column" }, 
							gap: "1rem",
							overflowX: { xs: "auto", sm: "auto", md: "auto", lg: "auto", xl: "visible" },
							overflowY: { xs: "visible", sm: "visible", md: "visible", lg: "visible", xl: "auto" }
						}}>
							{getEventsListResponse.results.slice(0, 10).map((eventItem) => (
								<Box sx={{ 
									flex: { xs: "0 0 auto", sm: "0 0 auto", md: "0 0 auto", lg: "0 0 auto", xl: "1" }, 
									minHeight: { xs: "115px", sm: "115px", md: "115px", lg: "115px", xl: "138px" },
									minWidth: { xs: "200px", sm: "200px", md: "200px", lg: "200px", xl: "auto" }
								}} key={eventItem.id}>
									<EventItemComponent eventDetails={eventItem} onFullscreen={handleShowFullscreenImage} />
								</Box>
							))}
						</Box>
					) : (
						<Box
							sx={{
								width: { xs: "100%", sm: "100%", md: "100%", lg: "100%", xl: "240px" },
								height: { xs: "100px", sm: "100px", md: "100px", lg: "100px", xl: "100%" },
								display: "flex",
								alignItems: "center",
								justifyContent: "center"
							}}
						>
							<Typography sx={{ fontSize: { xs: "0.875rem", sm: "0.875rem", md: "0.875rem", lg: "0.875rem", xl: "1rem" } }}>No Events</Typography>
						</Box>
					)}
				</Box>
			</Box>

			{fullscreenImage ? (
				<Box className="fullscreen-evidence-image-section" onClick={handleHideFullscreenImage}>
					<Box className="fullscreen-evidence-image-wrapper" onClick={(event) => event.stopPropagation()}>
						{fullscreenImage ? (
							<img
								src={`data:image/png;base64,${fullscreenImage}`}
								alt="Fullscreen"
								style={{ height: "100%", width: "100%", objectFit: "contain" }}
							/>
						) : null}
					</Box>

					<IconButton
						disableRipple
						sx={{ position: "absolute", top: "0", right: "0" }}
						onClick={handleHideFullscreenImage}
					>
						<ZoomInMapIcon sx={{ color: "white" }} />
					</IconButton>
				</Box>
			) : null}
		</Box>
	);
};

export default LiveView;
